import { Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { getFormattedDate } from '@mantine/dates';
import { ReactNode, useState } from 'react';
import PageLoader from 'src/UI/loader/PageLoader';
import { UnderlineTooltip } from 'src/UI/common';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { showError, showSuccess } from 'src/notifications/notifications';
import CashflowContentDeprecated from './CashflowContentDeprecated';
import AddNewBar from './components/AddNewBar';
import CashflowSection from './components/CashflowSection';
import CashflowTable, { CashflowColumn } from './components/CashflowTable';
import { ExpenditureModal } from './form/ExpenditureModal';
import { IncomeModal } from './form/IncomeModal';
import { LifeEventModal } from './form/LifeEventModal';
import useExpenditures, { EditableExpenditure, Expenditure } from './hooks/useExpenditures';
import useIncomes, { EditableIncome, Income } from './hooks/useIncomes';
import useLifeEvents, { LifeEvent } from './hooks/useLifeEvents';
import { labelForIncomeType } from './incomeTypes';
import { formatCurrency, Frequency, useTotalForFrequency } from './utils';

export default function CashflowContent() {
  const { flagEnabledFamilyCashflowsRevamp } = useFlags();
  if (!flagEnabledFamilyCashflowsRevamp) return <CashflowContentDeprecated />;
  return <CashflowContentInternal />;
}

function CashflowContentInternal() {
  const { isLoading: loadingIncomes, incomesData, upsertIncomes, deleteIncomes } = useIncomes();
  const {
    isLoading: loadingExpenditures,
    expendituresData,
    upsertExpenditures,
    deleteExpenditures,
  } = useExpenditures();
  const { lifeEventsData, upsertLifeEvents, deleteLifeEvents } = useLifeEvents();

  const [incomeFrequency, setIncomeFrequency] = useState<Frequency>('monthly');
  const [expenditureFrequency, setExpenditureFrequency] = useState<Frequency>('monthly');

  const [incomeModalOpened, { open: openIncomeModal, close: closeIncomeModal }] =
    useDisclosure(false);
  const [selectedIncome, setSelectedIncome] = useState<EditableIncome | undefined>();

  const [expenditureModalOpened, { open: openExpenditureModal, close: closeExpenditureModal }] =
    useDisclosure(false);
  const [selectedExpenditure, setSelectedExpenditure] = useState<EditableExpenditure | undefined>();

  const [lifeEventModalOpened, { open: openLifeEventModal, close: closeLifeEventModal }] =
    useDisclosure(false);
  const [selectedLifeEvent, setSelectedLifeEvent] = useState<LifeEvent | undefined>();

  const incomeMonthlyPreTax = useTotalForFrequency({
    items: incomesData,
    frequency: 'monthly',
    frequencyField: 'frequency',
    amountField: 'amountPreTax',
  });
  const incomeMonthlyPostTax = useTotalForFrequency({
    items: incomesData,
    frequency: 'monthly',
    frequencyField: 'frequency',
    amountField: 'amountPostTax',
  });
  const incomeAnnualPreTax = useTotalForFrequency({
    items: incomesData,
    frequency: 'annually',
    frequencyField: 'frequency',
    amountField: 'amountPreTax',
  });
  const incomeAnnualPostTax = useTotalForFrequency({
    items: incomesData,
    frequency: 'annually',
    frequencyField: 'frequency',
    amountField: 'amountPostTax',
  });

  if (loadingIncomes || loadingExpenditures) {
    return <PageLoader absolute={false} />;
  }

  const incomes = incomesData ?? [];
  const expenditures = expendituresData ?? [];
  const lifeEvents = lifeEventsData ?? [];

  const incomeIsMonthly = incomeFrequency === 'monthly';
  const incomeTotalPreTax = incomeIsMonthly ? incomeMonthlyPreTax : incomeAnnualPreTax;
  const incomeTotalPostTax = incomeIsMonthly ? incomeMonthlyPostTax : incomeAnnualPostTax;
  const incomeTotalTooltip = incomeIsMonthly
    ? `Annually: +${formatCurrency(incomeAnnualPreTax)} (${formatCurrency(incomeAnnualPostTax)} after tax)`
    : `Monthly: +${formatCurrency(incomeMonthlyPreTax)} (${formatCurrency(incomeMonthlyPostTax)} after tax)`;

  const expenditureAmountKey =
    expenditureFrequency === 'monthly' ? 'amountMonthly' : 'amountAnnually';
  const expenditureTotal = expenditures.reduce((sum, item) => sum + item[expenditureAmountKey], 0);
  const monthlyExpenditure = expenditures.reduce((sum, item) => sum + item.amountMonthly, 0);
  const annualExpenditure = expenditures.reduce((sum, item) => sum + item.amountAnnually, 0);
  const expenditureTotalTooltip =
    expenditureFrequency === 'monthly'
      ? `Annually: -${formatCurrency(annualExpenditure)}`
      : `Monthly: -${formatCurrency(monthlyExpenditure)}`;

  const handleSaveIncome = async (income: EditableIncome) => {
    try {
      await upsertIncomes([income]);
      showSuccess('Income saved successfully');
      closeIncomeModal();
      setSelectedIncome(undefined);
    } catch {
      showError('Failed to save income');
    }
  };

  const handleSaveExpenditure = async (
    expenditure: Omit<EditableExpenditure, 'id' | 'clientId'>,
  ) => {
    const item = { ...expenditure, id: selectedExpenditure?.id };
    try {
      await upsertExpenditures([item]);
      showSuccess('Expenditure saved successfully');
      closeExpenditureModal();
      setSelectedExpenditure(undefined);
    } catch {
      showError('Failed to save expenditure');
    }
  };

  const handleSaveLifeEvent = async (lifeEvent: Omit<LifeEvent, 'id' | 'clientId'>) => {
    const item = { ...lifeEvent, id: selectedLifeEvent?.id };
    try {
      await upsertLifeEvents([item]);
      showSuccess('Life event saved successfully');
      closeLifeEventModal();
      setSelectedLifeEvent(undefined);
    } catch {
      showError('Failed to save life event');
    }
  };

  return (
    <Stack gap="md">
      <AddNewBar
        actions={[
          {
            label: 'Income',
            testId: 'add-new-income',
            ariaLabel: 'Add new Income',
            onClick: () => {
              setSelectedIncome(undefined);
              openIncomeModal();
            },
          },
          {
            label: 'Expenditure',
            testId: 'add-new-expenditure',
            ariaLabel: 'Add new Expenditure',
            onClick: () => {
              setSelectedExpenditure(undefined);
              openExpenditureModal();
            },
          },
          {
            label: 'Life event',
            testId: 'add-new-life-event',
            ariaLabel: 'Add new Life event',
            onClick: () => {
              setSelectedLifeEvent(undefined);
              openLifeEventModal();
            },
          },
        ]}
      />

      <CashflowSection
        value="incomes"
        title="Income"
        count={incomes.length}
        frequency={incomeFrequency}
        onFrequencyChange={setIncomeFrequency}
        showFrequencyToggle
        totalNode={
          <SectionTotal tooltipLabel={incomeTotalTooltip}>
            +{formatCurrency(incomeTotalPreTax)} ({formatCurrency(incomeTotalPostTax)} after tax)
          </SectionTotal>
        }
      >
        <CashflowTable<Income>
          items={incomes}
          columns={incomeColumns(incomeFrequency)}
          onEdit={income => {
            setSelectedIncome(toEditableIncome(income));
            openIncomeModal();
          }}
          onDelete={income => income.id && deleteIncomes([income.id])}
        />
      </CashflowSection>

      <CashflowSection
        value="expenditures"
        title="Expenditure"
        count={expenditures.length}
        frequency={expenditureFrequency}
        onFrequencyChange={setExpenditureFrequency}
        showFrequencyToggle
        totalNode={
          <SectionTotal tooltipLabel={expenditureTotalTooltip}>
            -{formatCurrency(expenditureTotal)}
          </SectionTotal>
        }
      >
        <CashflowTable<Expenditure>
          items={expenditures}
          columns={expenditureColumns(expenditureFrequency)}
          onEdit={expenditure => {
            setSelectedExpenditure(toEditableExpenditure(expenditure));
            openExpenditureModal();
          }}
          onDelete={expenditure => expenditure.id && deleteExpenditures([expenditure.id])}
        />
      </CashflowSection>

      <CashflowSection
        value="lifeEvents"
        title="Future life events"
        count={lifeEvents.length}
        totalNode={
          <SectionTotal>
            -{formatCurrency(lifeEvents.reduce((sum, item) => sum + (item.cost || 0), 0))}
          </SectionTotal>
        }
      >
        <CashflowTable<LifeEvent>
          items={lifeEvents}
          columns={lifeEventColumns}
          onEdit={lifeEvent => {
            setSelectedLifeEvent(toEditableLifeEvent(lifeEvent));
            openLifeEventModal();
          }}
          onDelete={lifeEvent => lifeEvent.id && deleteLifeEvents([lifeEvent.id])}
        />
      </CashflowSection>

      <IncomeModal
        opened={incomeModalOpened}
        onClose={closeIncomeModal}
        onSubmit={handleSaveIncome}
        initialValues={selectedIncome}
      />
      <ExpenditureModal
        opened={expenditureModalOpened}
        onClose={closeExpenditureModal}
        onSubmit={handleSaveExpenditure}
        initialValues={selectedExpenditure}
      />
      <LifeEventModal
        opened={lifeEventModalOpened}
        onClose={closeLifeEventModal}
        onSubmit={handleSaveLifeEvent}
        initialValues={selectedLifeEvent}
      />
    </Stack>
  );
}

function toEditableIncome(income: Income): EditableIncome {
  return {
    id: income.id,
    clientId: income.clientId,
    description: income.description,
    incomeType: income.incomeType,
    frequency: income.frequency,
    amountPreTax: income.amountPreTax,
    amountPostTax: income.amountPostTax,
  };
}

function toEditableExpenditure(expenditure: Expenditure): EditableExpenditure {
  return {
    id: expenditure.id,
    clientId: expenditure.clientId,
    description: expenditure.description,
    category: expenditure.category,
    frequency: expenditure.frequency,
    amount: expenditure.amount,
  };
}

function toEditableLifeEvent(lifeEvent: LifeEvent): LifeEvent {
  return {
    id: lifeEvent.id,
    clientId: lifeEvent.clientId,
    description: lifeEvent.description,
    cost: lifeEvent.cost,
    details: lifeEvent.details,
    eventDate: lifeEvent.eventDate,
  };
}

function incomeColumns(frequency: Frequency): CashflowColumn<Income>[] {
  const preTaxKey = frequency === 'monthly' ? 'amountPreTaxMonthly' : 'amountPreTaxAnnually';
  const postTaxKey = frequency === 'monthly' ? 'amountPostTaxMonthly' : 'amountPostTaxAnnually';

  return [
    {
      key: 'incomeType',
      header: 'Type',
      renderValue: value => labelForIncomeType(String(value)),
    },
    { key: 'description', header: 'Source' },
    { key: 'frequency', header: 'Frequency' },
    {
      key: preTaxKey,
      header: 'Before tax',
      renderValue: (_value, item) =>
        frequencyAmountCell(frequency, item.amountPreTaxMonthly, item.amountPreTaxAnnually, {
          positive: true,
        }),
    },
    {
      key: postTaxKey,
      header: 'After tax',
      renderValue: (_value, item) =>
        frequencyAmountCell(frequency, item.amountPostTaxMonthly, item.amountPostTaxAnnually, {
          positive: true,
        }),
    },
  ];
}

function expenditureColumns(frequency: Frequency): CashflowColumn<Expenditure>[] {
  const amountKey = frequency === 'monthly' ? 'amountMonthly' : 'amountAnnually';

  return [
    { key: 'category', header: 'Category' },
    { key: 'frequency', header: 'Frequency' },
    {
      key: amountKey,
      header: `Spend (${frequency})`,
      renderValue: (_value, item) =>
        frequencyAmountCell(frequency, item.amountMonthly, item.amountAnnually),
    },
  ];
}

const lifeEventColumns: CashflowColumn<LifeEvent>[] = [
  { key: 'description', header: 'Description' },
  {
    key: 'eventDate',
    header: 'Date',
    renderValue: value =>
      getFormattedDate({
        type: 'default',
        date: new Date(String(value)),
        locale: 'en',
        format: 'D MMMM YYYY',
        labelSeparator: '',
      }),
  },
  { key: 'details', header: 'Details' },
  {
    key: 'cost',
    header: 'Amount',
    renderValue: value => formatCurrency(Number(value)),
  },
];

function frequencyAmountCell(
  frequency: Frequency,
  monthly: number,
  annually: number,
  options?: { positive?: boolean },
): ReactNode {
  const displayed = frequency === 'monthly' ? monthly : annually;
  const label =
    frequency === 'monthly'
      ? `Annually: ${formatCurrency(annually)}`
      : `Monthly: ${formatCurrency(monthly)}`;
  const prefix = options?.positive ? '+' : '';

  return (
    <UnderlineTooltip label={label}>
      {prefix}
      {formatCurrency(displayed)}
    </UnderlineTooltip>
  );
}

function SectionTotal({ children, tooltipLabel }: { children: ReactNode; tooltipLabel?: string }) {
  if (!tooltipLabel) {
    return (
      <Text size="lg" fw={FONT_WEIGHTS.bold} c="gray.10" style={{ whiteSpace: 'nowrap' }}>
        {children}
      </Text>
    );
  }

  return (
    <UnderlineTooltip label={tooltipLabel} size="lg" fw={FONT_WEIGHTS.bold}>
      {children}
    </UnderlineTooltip>
  );
}
